/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import { useMediaBatchPrepView } from "../../../../../hooks/useMediaBatchPrepView";
import { useEffect, useState } from "react";
import FineTunningHeader from "./partials/FineTunningHeader";
import FinalSolidificationAudit from "./partials/FinalSolidificationAudit";
import OptionalAdditives from "../../common/OptionalAddictives";
import { MediaFineTunningPayload } from "../../types/mediaFineTunning.types";
import SampleReference from "./partials/SampleReference";
import CustomToast from "../../../../../widgets/toaster";
import FineTuningFooter from "./partials/FineTuningFooter";
import { mediaPreparationService } from "../../../../../service/mediaPrepBatchServices";
import { useSearchParams } from "react-router-dom";
import type {ValidationErrors} from "../../types/mediaBatchPreparation.types";


interface Props {
	onBack: () => void;
	onNext: () => void;
	batchId: number | null;
}
const ProcessFineTuning = ({ onBack, onNext, batchId }: Props) => {
	const { t } = useTranslation();
	const { mediaBatchData, fetchBatchDetails } = useMediaBatchPrepView();

	const [fineTuningPayload, setFineTuningPayload] =
		useState<MediaFineTunningPayload>({
			additives: [],
			reagents: [],
			media_batch_id: batchId ?? 0,
			observed_final_ph: "",
			visual_standards_met: false,
			file_id: 0,
			remarks: "",
		});

	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});

	const [phVerified, setPhVerified] = useState(false);

	const handlePayloadChange = (
		field: string,
		value: string | number | boolean,
	) => {
		setFineTuningPayload((prev) => ({
			...prev,
			[field]: value,
		}));

		if (field === "observed_final_ph") {
			setErrors((prev) => ({
				...prev,
				observedPh: "",
			}));
		}
	};

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	const isPayloadValid = () => {
		return (
			fineTuningPayload.observed_final_ph !== "" &&
			fineTuningPayload.visual_standards_met !== undefined
		);
	};

	const [errors, setErrors] = useState<ValidationErrors>({
			powderBatch: "",
			powderLot: "",
			powderQuantity: "",
			solutionBatch: "",
			solutionLot: "",
			startTime: "",
			endTime: "",
			observedPh: "",
			visualStandards: "",
			additiveQuantity: "",
		});
	const validateFields = () => {
		const newErrors: ValidationErrors = {
			powderBatch: "",
			powderLot: "",
			powderQuantity: "",
			solutionBatch: "",
			solutionLot: "",
			startTime: "",
			endTime: "",
			observedPh: "",
			visualStandards: "",
			additiveQuantity: "",
		};

		let valid = true;
		if (!fineTuningPayload.observed_final_ph) {
			newErrors.observedPh = t("mediaBatchInit.errors.observePh");
			valid = false;
		}

		if (!phVerified) {
			newErrors.observedPh = t("mediaBatchInit.errors.verifyPh");
			valid = false;
		}

		if (!fineTuningPayload.visual_standards_met) {
			newErrors.visualStandards = t("mediaBatchInit.errors.visualStandards");
			valid = false;
		}

		const hasInvalidAdditive = [
			...fineTuningPayload.additives,
			...fineTuningPayload.reagents,
		].some((item) => !item.quantity || Number(item.quantity) <= 0);

		if (hasInvalidAdditive) {
			newErrors.additiveQuantity = t("mediaBatchInit.errors.additiveQuantity");
			valid = false;
		}

		setErrors(newErrors);
		return valid;
	};	

	const isReadOnly = mediaBatchData?.fine_tuning_details != null;
	const [batchList, setBatchList] = useState<any[]>([]);
	const [batchKeyword, setBatchKeyword] = useState("");
	const getBatchList = async () => {
		try {
			const response =
				await mediaPreparationService.mediaPrepgetBatchCodes(batchKeyword);

			if (response.status) {
				setBatchList(response.data);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const [, setSearchParams] = useSearchParams();
	const handleBatchChange = (batchId: string) => {
		const selectedBatch = batchList.find(
			(batch) => String(batch.id) === String(batchId)
		);
		if (!selectedBatch) return;

		// Don't navigate for completed batches
		if (
			selectedBatch.status === "Ready to Use" ||
			selectedBatch.status === "Rejected"
		) {
			return;
		}
		setSearchParams({ batchId });
	};

	useEffect(() => {
		if (!batchKeyword) {
			getBatchList();
			return;
		}

		const timer = setTimeout(() => {
			getBatchList();
		}, 300);

		return () => clearTimeout(timer);
	}, [batchId, batchKeyword]);

	useEffect(() => {
		if (batchId !== null) {
			fetchBatchDetails(batchId);
		}
	}, []);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaBatchInit.title")} />
			<div className="flex flex-col h-full bg-[#F8FAFC] min-h-screen">
				<FineTunningHeader onBack={onBack} />

				<div className="p-8 max-w-7xl mx-auto flex-1 overflow-y-auto space-y-6 animate-in fade-in duration-500">
					<div className="grid grid-cols-12 gap-4">
						<div className="col-span-12 lg:col-span-9 space-y-4">
							<FinalSolidificationAudit
								phVerified={phVerified}
								setPhVerified={setPhVerified}
								handlePayloadChange={handlePayloadChange}
								mediaBatchData={mediaBatchData}
								phReading={fineTuningPayload.observed_final_ph}
								isReadOnly={isReadOnly}
								batchList={batchList}
    							onBatchChange={handleBatchChange}
								errors={errors}
								setErrors={setErrors}
								onSearch={setBatchKeyword}
							/>
							<OptionalAdditives
								onChange={({ additives, reagents }) => {
									setFineTuningPayload((prev) => ({
										...prev,
										additives,
										reagents,
									}));

									setErrors((prev) => ({
										...prev,
										additiveQuantity: "",
									}));
								}}
								errors={errors}
								setErrors={setErrors}
							/>
							<SampleReference
								handlePayloadChange={handlePayloadChange}
							/>
						</div>
						<div className="col-span-12 lg:col-span-3 space-y-6">
							{/*<div className="bg-[#2D3E50] text-white p-6 rounded-2xl shadow-xl relative overflow-hidden">
								<h4 className="text-[10px] font-black uppercase tracking-widest mb-6 opacity-40">
									Prep Completion Stats
								</h4>
								<div className="space-y-6 relative z-10">
									<div className="flex justify-between items-end">
										<span className="text-[9px] font-black uppercase text-white/40">
											Total Preparation Time
										</span>
										<span className="text-xl font-black">
											2.4 Hours
										</span>
									</div>
									<div className="flex justify-between items-end">
										<span className="text-[9px] font-black uppercase text-white/40">
											Waste Percentage
										</span>
										<span className="text-xl font-black text-emerald-400">
											1.2%
										</span>
									</div>
									<div className="flex justify-between items-end">
										<span className="text-[9px] font-black uppercase text-white/40">
											Energy Consumption
										</span>
										<span className="text-xl font-black">
											12.5 kWh
										</span>
									</div>
								</div>
								<div className="absolute -bottom-6 -left-6 text-white/5">
									<span className="material-symbols-outlined text-9xl">
										analytics
									</span>
								</div>
							</div>*/}

							<div className="bg-orange-50 border border-orange-100 p-6 rounded-2xl">
								<div className="flex items-center gap-2 mb-4 text-orange-700">
									<span className="material-symbols-outlined text-sm">
										verified_user
									</span>
									<span className="text-[10px] font-black uppercase tracking-widest">
										Release Readiness
									</span>
								</div>
								<p className="text-[11px] text-orange-900 leading-relaxed">
									By clicking proceed, you are certifying that this
									batch has been visually inspected and is physically
									ready for Final Quality Review.
								</p>
							</div>
						</div>
					</div>
				</div>
				<FineTuningFooter
					preparationStatus={mediaBatchData?.status}
					onBack={onBack}
					onNext={onNext}
					fineTunningPayload={fineTuningPayload}
					showToast={showToast}
					validateFields={validateFields}
				/>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={2500}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default ProcessFineTuning;
